import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { pick, useLang } from '../i18n';
import { Turnstile, type TurnstileHandle } from '../components/Turnstile';
import { useAuth } from '../auth/AuthContext';
import { verifyEmail } from '../api/client';

const MARK = '/icons/favicon.svg';
/** Exported for `copy-parity.test.tsx`: every language must carry every key. */
export const T = {
  en: {
    title: 'Confirm your password to finish signing up',
    sub: 'Enter the password you chose when you created the account.',
    pw: 'Password', submit: 'Verify my email', working: 'Verifying…',
    signingIn: 'Email verified — signing you in…',
    ok: 'Email verified — sign in to continue.',
    wrong: 'That password does not match the one chosen when this account was created.',
    fail: 'This verification link is invalid or has expired.',
    busy: 'Too many attempts from your network just now. Your link is still valid — wait a moment and press the button again.',
    retry: 'We could not complete this just now. Your link is still valid — reload this page and try again.',
    reload: 'Reload and try again',
    login: 'Go to sign in', missing: 'This link is missing its token.',
  },
  es: {
    title: 'Confirma tu contraseña para terminar el registro',
    sub: 'Ingresa la contraseña que elegiste al crear la cuenta.',
    pw: 'Contraseña', submit: 'Verificar mi email', working: 'Verificando…',
    signingIn: 'Email verificado — te estamos ingresando…',
    ok: 'Email verificado — ingresa para continuar.',
    wrong: 'Esa contraseña no coincide con la que se eligió al crear esta cuenta.',
    fail: 'Este enlace de verificación es inválido o expiró.',
    busy: 'Demasiados intentos desde tu red ahora mismo. Tu enlace sigue siendo válido — espera un momento y vuelve a pulsar el botón.',
    retry: 'No pudimos completar esto ahora. Tu enlace sigue siendo válido — recarga esta página e inténtalo de nuevo.',
    reload: 'Recargar e intentar de nuevo',
    login: 'Ir al ingreso', missing: 'A este enlace le falta su token.',
  },
  fr: {
    title: 'Confirmez votre mot de passe pour terminer l’inscription',
    sub: 'Saisissez le mot de passe choisi lors de la création du compte.',
    pw: 'Mot de passe', submit: 'Vérifier mon email', working: 'Vérification…',
    signingIn: 'E-mail vérifié — connexion en cours…',
    ok: 'Email vérifié — connectez-vous pour continuer.',
    wrong: 'Ce mot de passe ne correspond pas à celui choisi à la création du compte.',
    fail: 'Ce lien de vérification est invalide ou a expiré.',
    busy: 'Trop de tentatives depuis votre réseau en ce moment. Votre lien reste valide — attendez un instant et appuyez à nouveau.',
    retry: 'Nous n’avons pas pu terminer pour le moment. Votre lien reste valide — rechargez cette page et réessayez.',
    reload: 'Recharger et réessayer',
    login: 'Aller à la connexion', missing: 'Ce lien n’a pas son jeton.',
  },
  pt: {
    title: 'Confirme sua senha para concluir o cadastro',
    sub: 'Digite a senha escolhida ao criar a conta.',
    pw: 'Senha', submit: 'Verificar meu email', working: 'Verificando…',
    signingIn: 'E-mail verificado — entrando…',
    ok: 'Email verificado — entre para continuar.',
    wrong: 'Essa senha não corresponde à escolhida quando esta conta foi criada.',
    fail: 'Este link de verificação é inválido ou expirou.',
    busy: 'Muitas tentativas da sua rede agora. Seu link continua válido — aguarde um instante e pressione o botão de novo.',
    retry: 'Não conseguimos concluir agora. Seu link continua válido — recarregue esta página e tente de novo.',
    reload: 'Recarregar e tentar de novo',
    login: 'Ir para o login', missing: 'Falta o token neste link.',
  },
};

/**
 * Verifying asks for the password, and does not run on load.
 *
 * Anyone can register an address they do not own, so a click proves only that you
 * read the mail — not that you are the person who signed up. Auto-verifying on
 * load meant a victim who merely opened the link activated a password a stranger
 * had chosen. Someone who never registered cannot get past this form.
 */
export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const { lang } = useLang();
  const t = pick(T, lang);
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [state, setState] = useState<'idle' | 'working' | 'signingIn' | 'ok' | 'wrong' | 'busy' | 'retry' | 'fail'>('idle');
  // The captcha `/auth/session` requires for a password sign-in. Usually invisible.
  const captcha = useRef<TurnstileHandle>(null);
  const { loginWithPassword } = useAuth();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || state === 'working') return;
    setState('working');
    try {
      const { email } = await verifyEmail(token, password);
      // Straight in, rather than back to a form asking for the password that was
      // just typed and just verified. Clicking the link proves you read the mail;
      // this form proves you are the person who registered — which is strictly more
      // than the sign-in page asks for, so it is signed in HERE, through the same
      // `/auth/session` password path with the same captcha and the same limits.
      // The API deliberately returns no session of its own ("verification is about
      // the address, and signing in is where a session comes from") and this does
      // not change that: it signs in.
      setState('signingIn');
      try {
        await loginWithPassword(email, password, await captcha.current?.getToken());
        nav('/app', { replace: true });
        return;
      } catch {
        // Verified either way — the address is stamped and the link is spent. A
        // sign-in that fails here (a captcha that never solved, a rate limit) must
        // not read as a failed verification, so it falls back to what this screen
        // did before: say it worked, and offer the sign-in page.
        setState('ok');
        setTimeout(() => nav('/login', { replace: true }), 1200);
        return;
      }
    } catch (err) {
      // A wrong password leaves the link usable — one typo must not cost a
      // legitimate user their registration.
      //
      // And a 429 is not a dead link. Every non-401 used to render "this link is
      // invalid or has expired", which for a rate-limited caller is simply false:
      // the link is fine, we refused to look at it. It is also a dead end — the
      // only routes out are registering again (409, the address is taken) and
      // forgot-password, metered on the same address — so a whole CGNAT could be
      // told their signup was broken. `busy` keeps the form up so they can press
      // again.
      //
      // And a request the API would not even LOOK at is not a dead link either.
      //
      // This page is served from a static bundle the browser caches. After a deploy
      // that changed what `/auth/verify-email` accepts, an open tab is still running
      // the old code — it posts the old shape, ajv refuses it with
      // `FST_ERR_VALIDATION`, and every non-401 landed on "invalid or has expired".
      // The link was fine; a reload fixes it; nothing said so, and the person had
      // already been told their signup was broken. Same for a 5xx or a response we
      // cannot parse: the server never got as far as judging the token.
      //
      // Safe to promise the link survives all of these: `consumeActionToken` runs
      // only AFTER the password verifies, so anything short of success — and short
      // of a 401, which is its own message — leaves the token unredeemed.
      //
      // A 400 in the API's OWN words is the real thing, and stays `fail`.
      const status = (err as { status?: number }).status;
      const code = (err as { code?: string }).code;
      const judged = status === 400 && code !== 'FST_ERR_VALIDATION';
      setState(status === 401 ? 'wrong' : status === 429 ? 'busy' : judged ? 'fail' : 'retry');
    }
  }

  return (
    <div className="auth-mini">
      <div className="card auth-mini__card" style={{ textAlign: 'left', maxWidth: 400 }}>
        <img className="brand-mark" src={MARK} alt="" width="30" height="30" style={{ marginBottom: 16 }} />

        {!token && (
          <div className="stack" style={{ gap: 16 }}>
            <p className="soft" style={{ fontSize: 15 }}>{t.missing}</p>
            <Link className="btn btn--black btn--sm" to="/login">{t.login}</Link>
          </div>
        )}

        {token && state === 'ok' && (
          <div className="stack" style={{ gap: 16 }}>
            <p className="soft" style={{ fontSize: 15 }}>{t.ok}</p>
            <Link className="btn btn--black btn--sm" to="/login">{t.login}</Link>
          </div>
        )}

        {token && state === 'fail' && (
          <div className="stack" style={{ gap: 16 }}>
            <p className="soft" style={{ fontSize: 15 }}>{t.fail}</p>
            <Link className="btn btn--black btn--sm" to="/login">{t.login}</Link>
          </div>
        )}

        {token && (state === 'idle' || state === 'working' || state === 'signingIn' || state === 'wrong' || state === 'busy' || state === 'retry') && (
          <form className="stack" style={{ gap: 14, textAlign: 'left' }} onSubmit={submit}>
            <h1 style={{ fontSize: 19, margin: 0 }}>{t.title}</h1>
            <p className="soft" style={{ fontSize: 14, margin: 0 }}>{t.sub}</p>
            {/* `auth-field` + `.input`, like `ResetPassword` on this same shell. The
                input carried no class at all, so it fell through to the browser's
                default box — squat, hairline-bordered, and nothing like the rest of
                the app — under a label whose class (`rv__k`) is not in the
                stylesheet, centred by the card. */}
            <div className="auth-field">
              <label htmlFor="verify-pw">{t.pw}</label>
              <input
                id="verify-pw" className="input"
                type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
              />
            </div>
            {state === 'wrong' && <p className="soft" style={{ fontSize: 14, color: 'var(--danger, #b4453c)' }}>{t.wrong}</p>}
            {state === 'busy' && <p className="soft" style={{ fontSize: 14 }}>{t.busy}</p>}
            {/* The form stays up, and the reload is offered rather than described:
                the whole point is that the running bundle may be the stale part, and
                pressing submit again would only re-send the same refused shape. */}
            {state === 'retry' && (
              <>
                <p className="soft" style={{ fontSize: 14 }}>{t.retry}</p>
                <button type="button" className="link-accent" onClick={() => window.location.reload()}>{t.reload}</button>
              </>
            )}
            <button className="btn btn--black btn--block" type="submit" disabled={state === 'working' || state === 'signingIn' || !password}>
              {state === 'signingIn' ? t.signingIn : state === 'working' ? t.working : t.submit}
            </button>
            {/* Last in the form, like the sign-in page: invisible until a challenge
                is needed, and then it appears below the button rather than moving it. */}
            <Turnstile ref={captcha} />
          </form>
        )}
      </div>
    </div>
  );
}
